/*
    Dependencies:
    - React
    - Typescript
    - react-router-dom
    - material-UI
    - lodash

    This component was designed to be used us a wrapper for material-ui input components.
    FormData is kept whitin this component, the save and get functions are passed trough as properties.

    - To bind the material-ui input children to the formData, you need to set three properties: name, value and onChange. 
    You should map onChange functions to the forms onChangeHandler methode and the values to the forms shadowData.
    For the name we use the key on the data model we want to bind. To access this methode, use react's build-in ref property.
       
        {thisForm: Form}
        <Form ref={(ref:Form)=>this.myForm = ref}>
            <TextField
                name = "firstName"
                value = {this.myForm.formShadowData.firstName}
                onChange={(e: any) => {this.myForm.onChangeHandler(e);}}
            />
        </Form>

    - The component requires a refreshHandler to refresh its parent. Make sure you handle of the opional callback. Your refresh handler should look like this
        
        refreshHandler={(callback?:()=>void)=>{
            this.forceUpdate(()=>{
                if (callback){callback()}
            })
        }}

    - We need the pass history of the router as a property. this will be used to intercept navigation when the form has unsaved changes.
    
    - We use the 'error' properties of material-ui for validation. (see https://material-ui.com/api/input/). 
    If this property is set to true for any of its children, the form will indicate somthing is wrong and you won't be able to save'

        <TextField
            name = "firstName"
            value = {this.myForm.formShadowData.firstName}
            onChange={(e: any) => {this.myForm.onChangeHandler(e);}}
            required = {true}
            error = {this.myForm.formShadowData.firstName.length <= 0}
        />
*/
import * as React from 'react';
import UnsavedChangesModal from './UnsavedChangesModal';
import Loading from './loading/Loading';
import { Button, Typography, Grid, Paper } from '@material-ui/core';
import * as _ from 'lodash';
import ErrorIcon from '@material-ui/icons/Error';

interface IProps {
  history: any
  disableUnsavedChangesModal?: boolean
  refreshHandler: (callback?: () => void) => void
  saveFormPromise?(formData: any): Promise<any> | null
  getFormPromise(): Promise<any> | null
}

interface IChangeHandlerOptions {
  itemRef?: object
  validation?: (value: any) => boolean
}

class Form extends React.Component<IProps, any> {

  public isLoading = true
  public usavedChangesModal = false
  public openDialog = false
  public isValid = true
  public formData: any
  public formShadowData: any
  public nextLocation: any
  public unblockNavigation: any;
  public validationObject: any;

  constructor(props: IProps) {
    super(props);
    this.getFormData = this.getFormData.bind(this);
    this.saveData = this.saveData.bind(this);
    this.discardChanges = this.discardChanges.bind(this);
    this.saveByModal = this.saveByModal.bind(this);
    this.discardByModal = this.discardByModal.bind(this);
    this.navigateTo = this.navigateTo.bind(this);
    this.validationObject = {};
  }

  public setNullToEmptyString(val: string | null) {
    if (val === null) { return "" }
    return val
  }

  public getFormData() {
    const promise = this.props.getFormPromise()
    if (promise) {
      promise
        .then((response: Response) => {
          if (typeof response.json === "function") {
            return response.json()
          }
          else {
            return response
          }
        })
        .then((data: object) => {
          Object.keys(data).forEach((key) => {
            data[key] = this.setNullToEmptyString(data[key])
          });
          this.formShadowData = data;
          this.formData = _.cloneDeep(data);
          this.isLoading = false;
          this.props.refreshHandler();
        });
    }
  }

  public createNavigationBlockListner = () => {
    this.unblockNavigation = this.props.history.block((nextLocation: any) => {
      if (!_.isEqual(this.formData, this.formShadowData)) {
        this.usavedChangesModal = true;
        this.nextLocation = nextLocation
        this.forceUpdate();
      }
      return (_.isEqual(this.formData, this.formShadowData));
    });
  }

  public componentDidMount() {
    this.getFormData();
    if (!this.props.disableUnsavedChangesModal) {
      this.createNavigationBlockListner();
    }
  }

  public componentWillUnmount() {
    if (this.unblockNavigation) { this.unblockNavigation(); }
  }

  public onChangeHandlerByKeyValue(key: string, value: any, options?: IChangeHandlerOptions) {
    if (options && options.itemRef !== undefined) {
      options.itemRef[key] = value;
    }
    else {
      this.formShadowData[key] = value;
    }
    this.props.refreshHandler(() => {
      if (options && typeof (options.validation) === 'function') {
        this.validationObject[key] = options.validation(value);
        this.isValid = this.validationCheck();
      }
      this.props.refreshHandler();
    });
  }

  public onChangeHandler(event: any, options?: IChangeHandlerOptions) {
    const inputElement = event.target
    let value: string | boolean;
    if (event.target.type === "checkbox") {
      value = event.target.checked;
    }
    else {
      value = event.target.value;
    }
    if (options) {
      this.onChangeHandlerByKeyValue(inputElement.name, value, options)
    }
    else {
      this.onChangeHandlerByKeyValue(inputElement.name, value)
    }
  }

  public async saveData() {
    this.isLoading = true;
    this.props.refreshHandler();
    let promiseResponse = null;
    if (this.props.saveFormPromise) {
      if (this.unblockNavigation) { this.unblockNavigation(); }
      promiseResponse = await this.props.saveFormPromise(this.formShadowData);
      promiseResponse.ok ? await this.getFormData() : this.isLoading = false;
      this.props.refreshHandler();
    }
    return promiseResponse
  }

  public discardChanges(callback?: () => void) {
    this.formShadowData = _.cloneDeep(this.formData);
    this.props.refreshHandler();
    if (callback) { callback() }
  }

  public navigateTo(location: any) {
    this.props.history.push(location)
  }

  public async saveByModal() {
    await this.saveData();
    this.navigateTo(this.nextLocation)
  }

  public discardByModal() {
    this.discardChanges(() => {
      this.props.history.push(this.nextLocation)
    })
  }

  public setValidation(inputElement: any) {
    if (inputElement.attributes && inputElement.attributes['aria-invalid'] && String(inputElement.attributes['aria-invalid'].value) === 'true') {
      this.validationObject[inputElement.name] = true;
    }
    else {
      this.validationObject[inputElement.name] = false;
    }
  }

  public validationCheck() {
    for (const key in this.validationObject) {
      if (this.validationObject.hasOwnProperty(key) && this.validationObject[key]) {
        return false
      }
    }
    return true
  }

  public render() {
    return (
      <Paper className="padding-24">
        {this.isLoading && <Loading />}
        {!this.isLoading &&
          <div className="padding-bottom-24">
            {this.props.children}
          </div>
        }
        {!this.isLoading && _.isEqual(this.formData, this.formShadowData) &&
          <Grid container={true} spacing={24} alignItems='center'>
            <Grid item={true}>
              <Typography variant='caption'>Form unchanged</Typography>
            </Grid>
          </Grid>
        }
        {!this.isLoading && !this.isValid && !_.isEqual(this.formData, this.formShadowData) &&
          <Grid container={true} spacing={8} alignItems='center' className="padding-bottom-15">
            <Grid item={true}>
              <Typography variant='caption'><ErrorIcon /></Typography>
            </Grid>
            <Grid item={true}>
              <Typography variant='caption'>You can't save until all changed fields are valid.</Typography>
            </Grid>
          </Grid>
        }
        {!this.isLoading && !_.isEqual(this.formData, this.formShadowData) &&
          <Grid container={true} spacing={24} alignItems='center'>
            <Grid item={true}>
              <Button variant="outlined" color='secondary' onClick={() => { this.saveData() }} disabled={!this.isValid}> Save </Button>
            </Grid>
            <Grid item={true}>
              <Button variant="outlined" color='secondary' onClick={() => { this.discardChanges() }}> Discard changes </Button>
            </Grid>
          </Grid>
        }
        <UnsavedChangesModal
          onClose={() => { this.usavedChangesModal = false; this.forceUpdate(); }}
          onSave={this.saveByModal}
          onDiscard={this.discardByModal}
          open={this.usavedChangesModal}
        />
      </Paper>
    );
  }
}

export default Form



// WEBPACK FOOTER //
// ./src/modules/shared/components/Form.tsx